import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Loader2,
  Briefcase,
  Sparkles,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Building2,
  MapPin,
  DollarSign,
  Calendar,
  Layers,
  Check,
  Search,
  Star,
  FileText,
} from "lucide-react";
import { careersService } from "@/api/careersService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

const slugify = (text) => {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

/**
 * Helper component for dynamic bullet lists (responsibilities, requirements, benefits)
 */
function DynamicListSection({ title, subtitle, items, setItems, placeholder }) {
  const [newItemText, setNewItemText] = useState("");

  const handleAdd = () => {
    if (!newItemText.trim()) return;
    setItems([...items, newItemText.trim()]);
    setNewItemText("");
  };

  const handleRemove = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleMove = (index, direction) => {
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= items.length) return;
    const copy = [...items];
    const [item] = copy.splice(index, 1);
    copy.splice(target, 0, item);
    setItems(copy);
  };

  const handleUpdate = (index, value) => {
    const copy = [...items];
    copy[index] = value;
    setItems(copy);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4 shadow-xs">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px] font-mono">
          {items.length} items
        </Badge>
      </div>

      {/* Existing Items */}
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 p-2.5 rounded-xl border border-slate-200 bg-slate-50 group hover:border-slate-300 transition-colors"
          >
            <div className="flex flex-col gap-0.5 text-slate-300 group-hover:text-slate-500">
              <button
                type="button"
                disabled={idx === 0}
                onClick={() => handleMove(idx, "up")}
                className="disabled:opacity-20 hover:text-cyan-600 transition-colors"
              >
                <ChevronUp className="w-3 h-3" />
              </button>
              <button
                type="button"
                disabled={idx === items.length - 1}
                onClick={() => handleMove(idx, "down")}
                className="disabled:opacity-20 hover:text-cyan-600 transition-colors"
              >
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>

            <Input
              value={item}
              onChange={(e) => handleUpdate(idx, e.target.value)}
              className="h-8 bg-transparent border-none text-xs text-slate-900 p-0 focus-visible:ring-0"
            />

            <button
              type="button"
              onClick={() => handleRemove(idx)}
              className="text-slate-300 hover:text-rose-600 p-1 transition-colors flex-shrink-0"
              title="Remove item"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Add New Item Input */}
      <div className="flex gap-2">
        <Input
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={placeholder || `Add an item...`}
          className="h-9 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white"
        />
        <Button
          type="button"
          onClick={handleAdd}
          variant="outline"
          size="sm"
          className="h-9 px-3 rounded-xl border-slate-200 bg-slate-50 hover:bg-slate-100 text-xs text-slate-700 flex-shrink-0"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add
        </Button>
      </div>
    </div>
  );
}

export default function AdminCareerEditor() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  // Catalogs
  const [departments, setDepartments] = useState([]);
  const [allTechnologies, setAllTechnologies] = useState([]);

  // Form State
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [isSlugManual, setIsSlugManual] = useState(false);
  const [departmentId, setDepartmentId] = useState("");
  const [employmentType, setEmploymentType] = useState("Full-time");
  const [workplaceType, setWorkplaceType] = useState("Remote");
  const [location, setLocation] = useState("Remote");

  // Experience & Compensation
  const [experienceMin, setExperienceMin] = useState(0);
  const [experienceMax, setExperienceMax] = useState("");
  const [salaryMin, setSalaryMin] = useState("");
  const [salaryMax, setSalaryMax] = useState("");
  const [salaryCurrency, setSalaryCurrency] = useState("INR");
  const [salaryVisible, setSalaryVisible] = useState(false);

  // Descriptions
  const [shortDescription, setShortDescription] = useState("");
  const [fullDescription, setFullDescription] = useState("");

  // Dynamic Lists
  const [responsibilities, setResponsibilities] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [preferredQualifications, setPreferredQualifications] = useState([]);
  const [benefits, setBenefits] = useState([]);

  // Skills Tagging
  const [selectedSkillIds, setSelectedSkillIds] = useState([]);
  const [techSearch, setTechSearch] = useState("");

  // Publishing & SEO
  const [status, setStatus] = useState("Draft");
  const [isFeatured, setIsFeatured] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(1);
  const [applicationDeadline, setApplicationDeadline] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");

  // Auto-slug sync unless manual override
  useEffect(() => {
    if (!isSlugManual && !isEditing) {
      setSlug(slugify(title));
    }
  }, [title, isSlugManual, isEditing]);

  // Load catalogs and initial job data
  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        setLoading(true);
        const [depts, techs] = await Promise.all([
          careersService.getDepartments(),
          careersService.getTechnologies(),
        ]);

        if (!mounted) return;
        setDepartments(depts || []);
        setAllTechnologies(techs || []);

        if (isEditing) {
          const job = await careersService.getJobById(id);
          if (!job) {
            toast.error("Position not found.");
            navigate("/admin/careers", { replace: true });
            return;
          }

          setTitle(job.title || "");
          setSlug(job.slug || "");
          setIsSlugManual(true);
          setDepartmentId(job.department_id || "");
          setEmploymentType(job.employment_type || "Full-time");
          setWorkplaceType(job.workplace_type || "Remote");
          setLocation(job.location || "Remote");
          setExperienceMin(job.experience_min ?? 0);
          setExperienceMax(job.experience_max ?? "");
          setSalaryMin(job.salary_min ?? "");
          setSalaryMax(job.salary_max ?? "");
          setSalaryCurrency(job.salary_currency || "INR");
          setSalaryVisible(Boolean(job.salary_visible));
          setShortDescription(job.short_description || "");
          setFullDescription(job.full_description || "");
          setResponsibilities(job.responsibilities || []);
          setRequirements(job.requirements || []);
          setPreferredQualifications(job.preferred_qualifications || []);
          setBenefits(job.benefits || []);
          setStatus(job.status || "Draft");
          setIsFeatured(Boolean(job.is_featured));
          setDisplayOrder(job.display_order ?? 1);
          setApplicationDeadline(
            job.application_deadline ? job.application_deadline.substring(0, 10) : ""
          );
          setMetaTitle(job.meta_title || "");
          setMetaDescription(job.meta_description || "");

          const linkedTechIds = (job.job_skills || []).map((s) => s.technology_id);
          setSelectedSkillIds(linkedTechIds);
        } else if (depts && depts.length > 0) {
          setDepartmentId(depts[0].id);
        }
      } catch (err) {
        console.error("[AdminCareerEditor] Load error:", err);
        toast.error("Failed to load position data.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    init();
    return () => {
      mounted = false;
    };
  }, [id, isEditing, navigate]);

  const toggleSkill = (techId) => {
    setSelectedSkillIds((prev) =>
      prev.includes(techId) ? prev.filter((id) => id !== techId) : [...prev, techId]
    );
  };

  const filteredTechnologies = allTechnologies.filter((t) =>
    t.name.toLowerCase().includes(techSearch.toLowerCase().trim())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Please enter a role title.");
      return;
    }

    if (!slug.trim()) {
      toast.error("Please provide a valid slug.");
      return;
    }

    if (!departmentId) {
      toast.error("Please select a department.");
      return;
    }

    if (!shortDescription.trim()) {
      toast.error("Please provide a short synopsis.");
      return;
    }

    if (!fullDescription.trim()) {
      toast.error("Please provide a comprehensive role overview.");
      return;
    }

    const payload = {
      title: title.trim(),
      slug: slug.trim(),
      department_id: departmentId,
      employment_type: employmentType,
      workplace_type: workplaceType,
      location: location.trim() || "Remote",
      experience_min: Number(experienceMin) || 0,
      experience_max: experienceMax ? Number(experienceMax) : null,
      salary_min: salaryMin ? Number(salaryMin) : null,
      salary_max: salaryMax ? Number(salaryMax) : null,
      salary_currency: salaryCurrency,
      salary_visible: salaryVisible,
      short_description: shortDescription.trim(),
      full_description: fullDescription.trim(),
      responsibilities,
      requirements,
      preferred_qualifications: preferredQualifications,
      benefits,
      application_deadline: applicationDeadline ? new Date(applicationDeadline).toISOString() : null,
      status,
      is_featured: isFeatured,
      display_order: Number(displayOrder) || 1,
      meta_title: metaTitle.trim() || `${title.trim()} Careers | Shiveda IT Services`,
      meta_description: metaDescription.trim() || shortDescription.trim(),
    };

    try {
      setSaving(true);
      if (isEditing) {
        await careersService.updateJob(id, payload, selectedSkillIds);
        toast.success(`Position "${title}" updated successfully.`);
      } else {
        await careersService.createJob(payload, selectedSkillIds);
        toast.success(`Position "${title}" created successfully.`);
      }
      navigate("/admin/careers");
    } catch (err) {
      console.error("[AdminCareerEditor] Save error:", err);
      toast.error(err.message || "Failed to save position.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          <span className="text-xs text-slate-500 font-medium">Loading position composer...</span>
        </div>
      </div>
    );
  }

  // Status badge helper
  const statusBadgeClass =
    status === "Published"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "Draft"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : status === "Closed"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <form onSubmit={handleSubmit} className="space-y-6 select-none max-w-5xl mx-auto pb-16">
      {/* Top Bar Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/careers")}
            className="h-9 w-9 p-0 border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 rounded-xl"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-cyan-600" />
              <span>{isEditing ? `Edit: ${title || "Position"}` : "Create Open Position"}</span>
              <Badge
                className={cn(
                  "text-[10px] uppercase font-mono ml-2 border",
                  statusBadgeClass
                )}
              >
                {status}
              </Badge>
            </h1>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">
              {isEditing ? `UUID: ${id}` : "Configure job specification, requirements, and publishing status."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/careers")}
            className="rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 text-xs h-9 px-4"
          >
            Cancel
          </Button>

          <Button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-xs font-semibold gap-2 h-9 px-5 shadow-sm"
          >
            {saving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                <span>{isEditing ? "Update Position" : "Save & Publish"}</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Grid: 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Columns: Core Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Card 1: Basic Information */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-3">
              <Briefcase className="w-4 h-4 text-cyan-600" />
              <span>Role Overview & Classification</span>
            </h2>

            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-700 font-semibold">Role Title *</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Senior Full Stack Developer"
                  className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs h-9 rounded-xl focus:bg-white"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-700 font-semibold">URL Slug *</Label>
                    <button
                      type="button"
                      onClick={() => setIsSlugManual(!isSlugManual)}
                      className="text-[10px] text-cyan-600 hover:underline font-semibold"
                    >
                      {isSlugManual ? "Auto-generate" : "Customize"}
                    </button>
                  </div>
                  <Input
                    value={slug}
                    onChange={(e) => {
                      setIsSlugManual(true);
                      setSlug(slugify(e.target.value));
                    }}
                    placeholder="senior-full-stack-developer"
                    className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs h-9 rounded-xl font-mono focus:bg-white"
                    required
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-700 font-semibold">Department *</Label>
                  <select
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    className="mt-1 w-full h-9 px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                  >
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id} className="bg-white text-slate-900">
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-700 font-semibold">Employment Type</Label>
                  <select
                    value={employmentType}
                    onChange={(e) => setEmploymentType(e.target.value)}
                    className="mt-1 w-full h-9 px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                  >
                    <option value="Full-time" className="bg-white text-slate-900">Full-time</option>
                    <option value="Part-time" className="bg-white text-slate-900">Part-time</option>
                    <option value="Contract" className="bg-white text-slate-900">Contract</option>
                    <option value="Internship" className="bg-white text-slate-900">Internship</option>
                  </select>
                </div>

                <div>
                  <Label className="text-xs text-slate-700 font-semibold">Workplace Type</Label>
                  <select
                    value={workplaceType}
                    onChange={(e) => setWorkplaceType(e.target.value)}
                    className="mt-1 w-full h-9 px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                  >
                    <option value="Remote" className="bg-white text-slate-900">Remote</option>
                    <option value="Hybrid" className="bg-white text-slate-900">Hybrid</option>
                    <option value="On-site" className="bg-white text-slate-900">On-site</option>
                  </select>
                </div>

                <div>
                  <Label className="text-xs text-slate-700 font-semibold">Location</Label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Remote or Noida, India"
                    className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs h-9 rounded-xl focus:bg-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-700 font-semibold">Short Synopsis (Card Blurb) *</Label>
                <Textarea
                  value={shortDescription}
                  onChange={(e) => setShortDescription(e.target.value)}
                  placeholder="Concise 1-2 sentence description displayed on the careers card and in meta tags..."
                  rows={2}
                  className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs rounded-xl focus:bg-white"
                  required
                />
              </div>

              <div>
                <Label className="text-xs text-slate-700 font-semibold">Full Role Overview *</Label>
                <Textarea
                  value={fullDescription}
                  onChange={(e) => setFullDescription(e.target.value)}
                  placeholder="Comprehensive explanation of the role, team context, mission, and day-to-day impact..."
                  rows={5}
                  className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs rounded-xl focus:bg-white"
                  required
                />
              </div>
            </div>
          </div>

          {/* Card 2: Dynamic Role Bullet Lists */}
          <DynamicListSection
            title="Core Responsibilities"
            subtitle="Key deliverables and leadership duties"
            items={responsibilities}
            setItems={setResponsibilities}
            placeholder="e.g. Architect and scale distributed backend APIs using Node.js..."
          />

          <DynamicListSection
            title="Essential Requirements"
            subtitle="Must-have experience, skills, and competencies"
            items={requirements}
            setItems={setRequirements}
            placeholder="e.g. 5+ years building production applications with React & TypeScript..."
          />

          <DynamicListSection
            title="Preferred Qualifications"
            subtitle="Nice-to-haves and bonus credentials"
            items={preferredQualifications}
            setItems={setPreferredQualifications}
            placeholder="e.g. Active open-source contributions or AWS certification..."
          />

          <DynamicListSection
            title="Perks & Benefits"
            subtitle="Compensation highlights and team advantages"
            items={benefits}
            setItems={setBenefits}
            placeholder="e.g. 100% Remote flexibility, health insurance, annual learning budget..."
          />
        </div>

        {/* Right 1 Column: Meta & Publishing Controls */}
        <div className="space-y-6">
          {/* Card 3: Publishing Lifecycle */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 shadow-xs">
            <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
              Publication Lifecycle
            </h3>

            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-700 font-semibold">Lifecycle Status</Label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="mt-1 w-full h-9 px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                >
                  <option value="Draft" className="bg-white text-slate-900">Draft (Invisible publicly)</option>
                  <option value="Published" className="bg-white text-slate-900">Published (Live on site)</option>
                  <option value="Closed" className="bg-white text-slate-900">Closed (Not accepting applications)</option>
                  <option value="Archived" className="bg-white text-slate-900">Archived (Historical reference)</option>
                </select>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <div className="text-xs font-semibold text-slate-900">Featured Position</div>
                  <div className="text-[10px] text-slate-500">Highlight role on career page</div>
                </div>
                <div
                  onClick={() => setIsFeatured((v) => !v)}
                  className={cn(
                    "w-10 h-5 rounded-full relative transition-colors cursor-pointer",
                    isFeatured ? "bg-cyan-600" : "bg-slate-200"
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-all",
                      isFeatured ? "left-5" : "left-0.5"
                    )}
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-700 font-semibold">Display Order</Label>
                <Input
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(e.target.value)}
                  min={1}
                  className="mt-1 bg-slate-50 border-slate-200 text-slate-900 text-xs h-9 rounded-xl focus:bg-white"
                />
              </div>

              <div>
                <Label className="text-xs text-slate-700 font-semibold">Application Deadline (Optional)</Label>
                <Input
                  type="date"
                  value={applicationDeadline}
                  onChange={(e) => setApplicationDeadline(e.target.value)}
                  className="mt-1 bg-slate-50 border-slate-200 text-slate-900 text-xs h-9 rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Card 4: Experience & Compensation */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 shadow-xs">
            <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
              Experience & Salary
            </h3>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] text-slate-700 font-semibold">Min Exp (Yrs)</Label>
                  <Input
                    type="number"
                    value={experienceMin}
                    onChange={(e) => setExperienceMin(e.target.value)}
                    min={0}
                    className="mt-1 bg-slate-50 border-slate-200 text-slate-900 text-xs h-9 rounded-xl focus:bg-white"
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-700 font-semibold">Max Exp (Yrs)</Label>
                  <Input
                    type="number"
                    value={experienceMax}
                    onChange={(e) => setExperienceMax(e.target.value)}
                    min={0}
                    placeholder="e.g. 6"
                    className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs h-9 rounded-xl focus:bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] text-slate-700 font-semibold">Min Salary (Annual)</Label>
                  <Input
                    type="number"
                    value={salaryMin}
                    onChange={(e) => setSalaryMin(e.target.value)}
                    placeholder="e.g. 1800000"
                    className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs h-9 rounded-xl focus:bg-white"
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-700 font-semibold">Max Salary (Annual)</Label>
                  <Input
                    type="number"
                    value={salaryMax}
                    onChange={(e) => setSalaryMax(e.target.value)}
                    placeholder="e.g. 2800000"
                    className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs h-9 rounded-xl focus:bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] text-slate-700 font-semibold">Currency</Label>
                  <select
                    value={salaryCurrency}
                    onChange={(e) => setSalaryCurrency(e.target.value)}
                    className="mt-1 w-full h-9 px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                  >
                    <option value="INR" className="bg-white text-slate-900">INR (₹)</option>
                    <option value="USD" className="bg-white text-slate-900">USD ($)</option>
                    <option value="EUR" className="bg-white text-slate-900">EUR (€)</option>
                    <option value="GBP" className="bg-white text-slate-900">GBP (£)</option>
                  </select>
                </div>

                <div className="flex flex-col justify-end">
                  <label className="flex items-center gap-2.5 cursor-pointer pb-2 text-[11px] text-slate-700 font-semibold select-none group">
                    <div
                      onClick={() => setSalaryVisible((v) => !v)}
                      className={cn(
                        "w-8 h-4 rounded-full relative transition-colors cursor-pointer",
                        salaryVisible ? "bg-cyan-600" : "bg-slate-200"
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-xs transition-all",
                          salaryVisible ? "left-4" : "left-0.5"
                        )}
                      />
                    </div>
                    <span className="group-hover:text-cyan-700 transition-colors">Publicly visible</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Card 5: Technology Skills Picker */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3 shadow-xs">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                Required Tech Stack
              </h3>
              <Badge className="bg-cyan-50 text-cyan-700 border-cyan-200 text-[10px] font-mono">
                {selectedSkillIds.length} selected
              </Badge>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={techSearch}
                onChange={(e) => setTechSearch(e.target.value)}
                placeholder="Search technologies..."
                className="pl-8 h-8 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white"
              />
            </div>

            <div className="max-h-48 overflow-y-auto pr-1 space-y-1">
              {filteredTechnologies.map((tech) => {
                const isSelected = selectedSkillIds.includes(tech.id);
                return (
                  <button
                    key={tech.id}
                    type="button"
                    onClick={() => toggleSkill(tech.id)}
                    className={cn(
                      "w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors text-left",
                      isSelected
                        ? "bg-cyan-50 text-cyan-700 font-semibold border border-cyan-200"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    )}
                  >
                    <span>{tech.name}</span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-cyan-600" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Card 6: SEO Metadata */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3 shadow-xs">
            <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
              SEO & Social Meta
            </h3>

            <div>
              <Label className="text-[11px] text-slate-700 font-semibold">Meta Title</Label>
              <Input
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                placeholder={`${title || "Role"} Careers | Shiveda IT Services`}
                className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs h-8 rounded-xl focus:bg-white"
              />
            </div>

            <div>
              <Label className="text-[11px] text-slate-700 font-semibold">Meta Description</Label>
              <Textarea
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder="Search engine synopsis..."
                rows={3}
                className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs rounded-xl focus:bg-white"
              />
            </div>
          </div>

          {/* Bottom Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/careers")}
              className="rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 text-xs h-10 px-5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-xs font-semibold gap-2 h-10 px-6 shadow-sm"
            >
              {saving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  <span>{isEditing ? "Update Position" : "Save & Publish"}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
